import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { cn } from "@/common/utils/css.utils";
import { DisplayCurrencyName } from "@/features/display-currency/components/DisplayCurrencyName";
import { DISPLAY_CURRENCIES } from "@/features/display-currency/display-currency.sync";
import {
  useDisplayCurrencyEnabled,
  usePickedDisplayCurrency,
} from "@/features/display-currency/useDisplayCurrency";
import { useSetDisplayCurrency } from "@/features/display-currency/useSetDisplayCurrency";
import { isUsLocale } from "@/i18n/config";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";
import { ChevronDown as ChevronDownIcon } from "@whoppah/icons/24/ChevronDown";

/**
 * Header display-currency picker, next to the language switcher (WHO-5143). A Radix DropdownMenu
 * with radio items (the repo's menu convention): it opens on click or tap, and the keyboard can
 * reach, move through and pick the options. Hidden while the `web_display_currency` gate is off,
 * and until the browser has read the buyer's currency. Not on `/en-us` (WHO-6510): the address
 * sets USD there, so a pick would change nothing on the page.
 */
const HeaderCurrencySwitcher = () => {
  const { t } = useTranslation();
  const enabled = useDisplayCurrencyEnabled();
  const picked = usePickedDisplayCurrency();
  const setDisplayCurrency = useSetDisplayCurrency();
  const { locale } = useRouter();

  if (!enabled || !picked || isUsLocale(locale)) {
    return null;
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className="group cursor-pointer pb-4 font-title text-sm font-bold uppercase"
        aria-label={t("common:currency-setting-title", "Currency")}
        data-testid="header-currency-switcher"
      >
        <span className="flex group-hover:text-attention-500 group-data-[state=open]:text-attention-500">
          <span>{picked}</span>
          <ChevronDownIcon className="h-5 w-5" />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          align="center"
          className="z-[100] flex flex-col space-y-2 border bg-white p-5 data-[state=closed]:animate-menu-hide data-[state=open]:animate-menu-show"
        >
          <DropdownMenuRadioGroup
            value={picked}
            onValueChange={value =>
              setDisplayCurrency(value as (typeof DISPLAY_CURRENCIES)[number])
            }
            className="flex flex-col space-y-2"
          >
            {DISPLAY_CURRENCIES.map(currency => (
              <DropdownMenuRadioItem
                key={currency}
                value={currency}
                className={cn(
                  "cursor-pointer font-title text-sm outline-none hover:text-attention-500 data-[highlighted]:text-attention-500",
                  picked === currency
                    ? "font-bold text-attention-500"
                    : "font-normal"
                )}
              >
                <DisplayCurrencyName currency={currency} />
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
};

export default HeaderCurrencySwitcher;
